/**
 * Lo que decide la vista de la agencia PAGA en Agencia › Suscripción (tanda «Suscripción y pagos», UD-16).
 * Lógica pura: sin React, sin pedidos y sin i18n propio (los textos del chat reciben `t`). Los componentes
 * dibujan lo que esto devuelve, y los tests miran acá.
 *
 * Dos reglas que sostienen la pantalla:
 * - **Ningún número se inventa** (contrato §0). Sin `active_comm_agree.price` no hay precio, y el plan actual
 *   NUNCA cae a la lista de `pricingConfig.js`: es el bug de cc#208 (la agencia veía la lista como si fuera
 *   lo que paga). La lista sirve sólo para los OTROS planes y para el pago anual.
 * - **Tolera al Backend viejo** (contrato §7). Sin `usage`, `price`, `display_state` ni `is_agency_admin`,
 *   cada función contesta «no hay» y la pieza no se dibuja; «vencida» no se deduce acá, viene de Tesla.
 */
import {PERIODS, PLANS} from '../../../upgrade_to_dashbo/pricingConfig';

export const NS = 'subscription_page';
// Los valores interpolados viajan como TEXTO (React o el chat): sin esto i18next los escapa a entidades.
const RAW = {escapeValue: false};

// ── Formatos ───────────────────────────────────────────────────────────────────────────────────

export function localeOf(lang) {
    return String(lang || 'es').toLowerCase().startsWith('en') ? 'en-US' : 'es-AR';
}

/**
 * `YYYY-MM-DD` se arma a mano como fecha LOCAL: `new Date('2026-11-01')` es medianoche UTC, que en
 * Argentina es el día anterior (el mismo cuidado que `subscriptionDates.js`). Con hora (ISO 8601 del
 * proveedor) sí va por el constructor y queda en la zona del navegador.
 */
export function parseDate(value) {
    if (!value) return null;
    const text = String(value);
    const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (plain) return new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]));
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** «1 de noviembre» (con año si no es el año en curso). */
export function formatLongDate(value, lang, today = new Date()) {
    const date = parseDate(value);
    if (!date) return '';
    const options = {day: 'numeric', month: 'long'};
    if (date.getFullYear() !== today.getFullYear()) options.year = 'numeric';
    return new Intl.DateTimeFormat(localeOf(lang), options).format(date);
}

/** «1/11», el formato de las tablas. */
export function formatShortDate(value, lang) {
    const date = parseDate(value);
    if (!date) return '';
    return new Intl.DateTimeFormat(localeOf(lang), {day: 'numeric', month: 'numeric'}).format(date);
}

function capitalize(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** «Octubre de 2026» / «October 2026». */
export function formatMonth(date, lang) {
    return capitalize(new Intl.DateTimeFormat(localeOf(lang), {month: 'long', year: 'numeric'}).format(date));
}

/**
 * «USD 100» o «ARS 98.400»: sin decimales si es entero, con dos si no. `decimals: true` fuerza los dos
 * (la tabla de pagos). `''` si no hay monto: nunca «USD NaN».
 */
export function formatAmount(amount, currency, lang, {decimals = false} = {}) {
    if (amount === null || amount === undefined || amount === '') return '';
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    const digits = decimals || !Number.isInteger(value) ? 2 : 0;
    const number = new Intl.NumberFormat(localeOf(lang), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(value);
    return `${String(currency || 'USD').toUpperCase()} ${number}`;
}

// ── Planes y precio ────────────────────────────────────────────────────────────────────────────

export const PLAN_IDS = PLANS.map((plan) => plan.id);

/** Las variantes `<plan>_with_looker` son el mismo plan desde el pricing de octubre. */
export function baseTier(tier) {
    return tier ? String(tier).replace(/_with_looker$/, '') : null;
}

export function planOf(tier) {
    const id = baseTier(tier);
    return PLANS.find((plan) => plan.id === id) || null;
}

/** `custom`, un tier desconocido o ninguno: no hay grilla con qué compararlo. */
export function isCustomPlan(tier) {
    return !planOf(tier);
}

export function nextPlanOf(tier) {
    const index = PLAN_IDS.indexOf(baseTier(tier));
    return index >= 0 && index < PLANS.length - 1 ? PLANS[index + 1] : null;
}

/** `'up'`, `'down'`, `'same'` o `null` (plan actual a medida: no hay orden). */
export function planDirection(currentTier, targetId) {
    const from = PLAN_IDS.indexOf(baseTier(currentTier));
    const to = PLAN_IDS.indexOf(targetId);
    if (from < 0 || to < 0) return null;
    if (to === from) return 'same';
    return to > from ? 'up' : 'down';
}

/** El precio de lista POR MES de un plan, en una moneda y un período, o `null`. */
export function listMonthly(planId, currency, period) {
    const plan = PLANS.find((item) => item.id === planId);
    const prices = plan && plan.prices[String(currency || '').toLowerCase()];
    const value = prices ? prices[period] : undefined;
    return Number.isFinite(value) ? value : null;
}

/** El descuento del período (−10 %, −20 %, −30 %), o 0. */
export function periodDiscount(period) {
    return (PERIODS[period] && PERIODS[period].discount) || 0;
}

export const PERIOD_IDS = ['monthly', 'trimestral', 'semestral', 'yearly'];
export const CURRENCY_IDS = ['USD', 'ARS'];

/**
 * La versión siguiente del convenio (`price.next`, Tesla, tanda «Baja con descuento» §1.4): desde cuándo rige
 * y cuánto sale. `null` si no viene o no tiene forma. Sin versión siguiente Tesla NO manda la clave (no la
 * manda en `null`, contrato §8): ausente y `null` son lo mismo. Si trae moneda, va con ella.
 */
function nextPriceOf(raw) {
    if (!raw || typeof raw !== 'object' || typeof raw.from_date !== 'string') return null;
    if (raw.amount === null || raw.monthly_equivalent === null) return null;
    const amount = Number(raw.amount);
    const monthlyEquivalent = Number(raw.monthly_equivalent);
    if (!Number.isFinite(amount) || !Number.isFinite(monthlyEquivalent)) return null;
    const next = {fromDate: raw.from_date, amount, monthlyEquivalent};
    if (raw.currency) next.currency = String(raw.currency).toUpperCase();
    return next;
}

/**
 * Lo que paga la agencia, de `active_comm_agree.price` (Tesla, contrato §1.1). `null` si no viene o no
 * tiene forma: la pantalla dibuja el nombre del plan solo. Nunca se completa con la lista.
 *
 * `next` (la versión siguiente) va sólo si Tesla la manda: con Tesla viejo, o sin cambio programado, el
 * precio es el de UD-16 tal cual.
 */
export function priceOf(billingData) {
    const raw = billingData && billingData.active_comm_agree && billingData.active_comm_agree.price;
    if (!raw || typeof raw !== 'object') return null;
    const amount = Number(raw.amount);
    const monthlyEquivalent = Number(raw.monthly_equivalent);
    if (raw.amount === null || raw.monthly_equivalent === null) return null;
    if (!Number.isFinite(amount) || !Number.isFinite(monthlyEquivalent) || !raw.currency) return null;
    const price = {
        amount,
        monthlyEquivalent,
        currency: String(raw.currency).toUpperCase(),
        period: PERIOD_IDS.includes(raw.period) ? raw.period : null,
        plusVat: raw.plus_vat === true,
    };
    const next = nextPriceOf(raw.next);
    if (next) price.next = next;
    return price;
}

// ── El descuento de retención (tanda «Baja con descuento», UD-17, contrato §3.2) ─────────────────

function isIsoDate(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function sameAmount(a, b) {
    return Math.abs(a - b) < 0.005;
}

/** `active_retention_offer` de `agency/subscription/` (contrato §2.5), o `null` (Backend viejo o sin oferta). */
function activeRetentionOfferOf(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const amount = Number(raw.amount);
    const fullAmount = Number(raw.full_amount);
    if (raw.amount === null || raw.full_amount === null) return null;
    if (!Number.isFinite(amount) || !Number.isFinite(fullAmount) || !raw.currency) return null;
    if (typeof raw.percent !== 'number' || !Number.isFinite(raw.percent)) return null;
    if (!isIsoDate(raw.starts_on) || !isIsoDate(raw.ends_on) || !isIsoDate(raw.back_to_full_on)) return null;
    return {
        percent: raw.percent,
        amount,
        fullAmount,
        currency: String(raw.currency).toUpperCase(),
        plusVat: raw.plus_vat === true,
        startsOn: raw.starts_on,
        endsOn: raw.ends_on,
        backToFullOn: raw.back_to_full_on,
    };
}

/**
 * El descuento aceptado, SÓLO si el convenio lo confirma (contrato §3.2); si no, `null` y la pantalla es la
 * de UD-16, como si no hubiera oferta. `state`:
 * - `'upcoming'`: todavía no empezó. Hoy paga el precio pleno y la versión siguiente es la del descuento,
 *   desde `starts_on` y por `amount`.
 * - `'active'`: en curso. Hoy paga `amount` y la versión siguiente es la vuelta, desde `back_to_full_on` y
 *   por `full_amount`.
 *
 * Se confirman también el precio de HOY y la moneda: la tarjeta dibuja `price` en grande al lado de la línea
 * del descuento, y no pueden contradecirse. Si alguien canceló la vuelta desde el CC, `price.next` deja de
 * coincidir y la tarjeta vuelve a la regla de UD-16.
 */
export function retentionOf(activeRetentionOffer, price) {
    const offer = activeRetentionOfferOf(activeRetentionOffer);
    if (!offer || !price || !price.next || price.currency !== offer.currency) return null;
    const {next} = price;
    if (next.currency && next.currency !== offer.currency) return null;
    if (next.fromDate === offer.startsOn && sameAmount(next.monthlyEquivalent, offer.amount)
        && sameAmount(price.monthlyEquivalent, offer.fullAmount)) {
        return {...offer, state: 'upcoming'};
    }
    if (next.fromDate === offer.backToFullOn && sameAmount(next.monthlyEquivalent, offer.fullAmount)
        && sameAmount(price.monthlyEquivalent, offer.amount)) {
        return {...offer, state: 'active'};
    }
    return null;
}

/**
 * ¿El cobro de esa fecha cae en o después del próximo cambio de precio del descuento (el inicio, antes de
 * empezar; la vuelta al pleno, durante)? Entonces el monto de hoy no vale para ese cobro, y el «Próximo cobro»
 * va con la fecha sola, como cuando el proveedor cobró otro monto (C-sp-fe-02). Sin descuento, `false`.
 */
export function chargeCrossesRetentionChange(retention, chargeDate) {
    if (!retention) return false;
    const charge = parseDate(chargeDate);
    const change = parseDate(retention.state === 'upcoming' ? retention.startsOn : retention.backToFullOn);
    if (!charge || !change) return true;
    return startOfDay(charge) >= change;
}

/**
 * Un monto del descuento dentro de una frase: «USD 50 más IVA» si el convenio va con IVA, igual que el precio
 * grande de la tarjeta (P-1 de bd-fe, fede 26/10). El IVA sale del convenio (`price.plusVat`), no de la moneda.
 */
export function retentionAmountText(t, amount, currency, plusVat, lang) {
    const text = formatAmount(amount, currency, lang);
    return plusVat ? `${text} ${t(`${NS}.plan.plus_vat`)}` : text;
}

/** `'upcoming' | 'active' | null` (ver `retentionOf`). */
export function retentionStateOf(activeRetentionOffer, price) {
    const retention = retentionOf(activeRetentionOffer, price);
    return retention ? retention.state : null;
}

/**
 * El pago anual se ofrece sólo si ahorra de verdad: la agencia paga mes a mes, su plan está en la grilla y
 * la lista anual en SU moneda es menor que lo que paga hoy. El ahorro es contra lo que paga, no contra la
 * lista mensual.
 *
 * Con un descuento de retención confirmado (`retention`) no se ofrece: el convenio ya tiene programadas sus
 * dos versiones, y el ahorro se calcularía contra un precio con descuento.
 */
export function annualOffer(tier, price, retention = null) {
    if (retention) return null;
    if (!price || price.period !== 'monthly' || isCustomPlan(tier)) return null;
    const monthly = listMonthly(baseTier(tier), price.currency, 'yearly');
    if (monthly === null || !(monthly < price.monthlyEquivalent)) return null;
    return {
        monthly,
        total: monthly * 12,
        savings: (price.monthlyEquivalent - monthly) * 12,
        currency: price.currency,
    };
}

/**
 * «USD 140 más que hoy»: sólo si se mira en la MISMA moneda y el MISMO período de lo que paga; si no, la
 * resta mezclaría cosas que no se comparan.
 *
 * Con un descuento de retención confirmado (`retention`), contra el precio PLENO (`full_amount`): el
 * descuento dura dos meses, y un plan nuevo se compara con lo que la agencia va a pagar después.
 */
export function planDelta(planId, price, currency, period, retention = null) {
    if (!price || String(currency).toUpperCase() !== price.currency || period !== price.period) return null;
    const list = listMonthly(planId, currency, period);
    if (list === null) return null;
    const base = retention ? retention.fullAmount : price.monthlyEquivalent;
    const difference = list - base;
    if (Math.abs(difference) < 0.005) return null;
    return {amount: Math.abs(difference), currency: price.currency, more: difference > 0};
}

/** Período y moneda con que abre «Cambiar de plan»: los de lo que paga, si se saben. */
export function defaultChooserState(price, subscriptionCurrency) {
    const subscription = String(subscriptionCurrency || '').toUpperCase();
    const currency = price && CURRENCY_IDS.includes(price.currency)
        ? price.currency
        : (CURRENCY_IDS.includes(subscription) ? subscription : 'USD');
    return {period: (price && price.period) || 'monthly', currency};
}

// ── Lo que usa ─────────────────────────────────────────────────────────────────────────────────

export const NEAR_LIMIT = 0.9;
export const ROOM_BADGE_FROM = 0.8;

function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

/** `usage` de `agency/subscription/` (contrato §2.1), o `null` con el Backend viejo. */
export function usageOf(subscriptionData) {
    const usage = subscriptionData && subscriptionData.usage;
    if (!usage || typeof usage !== 'object') return null;
    return usage;
}

/**
 * El medidor de un uso con tope: `ok` (< 90 %), `near` (desde 90 %) o `full` (sin lugar). `null` sin tope
 * (los usuarios del equipo, D-6, o un tope en 0): un medidor contra cero sólo puede pintar rojo, y sería una
 * alarma de algo que el plan no trae.
 */
export function meterOf(entry) {
    if (!entry || !isNumber(entry.used) || !isNumber(entry.limit) || entry.limit <= 0) return null;
    const {used, limit} = entry;
    const left = Math.max(0, isNumber(entry.available) ? entry.available : limit - used);
    const ratio = used / limit;
    let state = 'ok';
    if (left <= 0 || ratio >= 1) state = 'full';
    else if (ratio >= NEAR_LIMIT) state = 'near';
    return {used, limit, left, ratio, percent: Math.min(100, Math.round(ratio * 100)), state};
}

/** El aviso de cupo de cuentas: desde el 90 %. */
export function quotaNoticeOf(usage) {
    const accounts = meterOf(usage && usage.ad_accounts);
    return accounts && accounts.state !== 'ok' ? accounts : null;
}

/** «Te da margen para crecer» en el plan siguiente, sólo con las cuentas al 80 % o más. */
export function showsRoomBadge(usage) {
    const accounts = meterOf(usage && usage.ad_accounts);
    return !!accounts && accounts.ratio >= ROOM_BADGE_FROM;
}

/**
 * Las cantidades que muestra la tarjeta del plan ACTUAL: los topes reales de la agencia (los negociados
 * incluidos), los mismos de los medidores y de «Te quedan». La lista decía 300 cuentas en Epic y el medidor
 * 500 (QA-spfe-N01). Los usuarios van con los del plan: todavía no tienen tope propio (D-6).
 */
export function currentQuantitiesOf(plan, usage) {
    const quantities = {...plan.quantities};
    const accounts = usage && usage.ad_accounts && usage.ad_accounts.limit;
    if (isNumber(accounts) && accounts > 0) {
        quantities.linkedAccounts = accounts;
        quantities.approxClients = Math.floor(accounts / 2);
    }
    const portal = usage && usage.portal_accesses && usage.portal_accesses.limit;
    if (isNumber(portal) && portal > 0) quantities.portalAccesses = portal;
    return quantities;
}

/** La línea «te quedan» de cada plan en «Cambiar de plan» (`plans.fit_*`). */
export function planFit(plan, {isCurrent, usage}) {
    const accounts = usage && usage.ad_accounts;
    if (!accounts || !isNumber(accounts.used)) return null;
    if (isCurrent) {
        const meter = meterOf(accounts);
        return meter ? {kind: 'left', count: meter.left} : null;
    }
    const limit = plan.quantities.linkedAccounts;
    if (accounts.used > limit) return {kind: 'over', used: accounts.used, limit};
    return {kind: 'room', count: limit - accounts.used};
}

export const EXTRA_BLOCK_SIZE = 10;

/**
 * Lo que sale por mes cada adicional: 10 cuentas y un usuario más (pricing de octubre, los mismos del sitio). En
 * pesos es USD × 1.640 más IVA, como la lista de `pricingConfig.js`; cualquier otra moneda va en dólares.
 */
export const ADDON_PRICES = {
    accounts: {USD: 20, ARS: 32800},
    user: {USD: 10, ARS: 16400},
};

/**
 * «USD 20 por mes» o «ARS 32.800 + IVA por mes», en la moneda que se está mirando. `times` multiplica: al bajar
 * de plan, los bloques de 10 cuentas que quedan afuera.
 */
export function addonPriceText(t, addon, {currency, lang, times = 1} = {}) {
    const code = String(currency || '').toUpperCase() === 'ARS' ? 'ARS' : 'USD';
    const amount = formatAmount(ADDON_PRICES[addon][code] * times, code, lang);
    return t(`${NS}.plans.addon_price_${code.toLowerCase()}`, {amount, interpolation: RAW});
}

/** Al bajar: las cuentas que quedan afuera, en bloques de 10 (el precio lo pone `addonPriceText`). `null` si entran. */
export function downgradeOverage(plan, usage) {
    const used = usage && usage.ad_accounts && usage.ad_accounts.used;
    if (!isNumber(used)) return null;
    const extra = used - plan.quantities.linkedAccounts;
    if (extra <= 0) return null;
    return {used, limit: plan.quantities.linkedAccounts, blocks: Math.ceil(extra / EXTRA_BLOCK_SIZE)};
}

/** Al bajar: los usuarios de más. Sólo informativo: todavía no se cobran (D-6). */
export function downgradeUsers(plan, usage) {
    const used = usage && usage.team_users && usage.team_users.used;
    const limit = plan.quantities.teamUsers;
    if (!isNumber(used) || limit === null || used <= limit) return null;
    return {used, limit};
}

// ── Medio de pago ──────────────────────────────────────────────────────────────────────────────

// `payment_method` de Tesla (`CommAgree.PAYMENT_METHOD_*`) → la clave del nombre que ve la agencia.
const METHOD_KEYS = {
    'Paypal - Suscripción': 'paypal_subscription',
    'MercadoPago AR - Pago automático': 'mp_subscription',
    'Paypal - Envío manual': 'paypal_link',
    'MercadoPago - Cupón': 'mp_link',
    'Transferencia Bancaria': 'transfer',
    'Payoneer - Envío manual': 'payoneer',
    'Binance - USDT': 'binance',
};

/** La clave del nombre del medio (`method.*`); un valor desconocido es «Otro medio acordado». */
export function paymentMethodKey(raw) {
    return METHOD_KEYS[raw] || 'other';
}

/** La suscripción de cobro automático (PayPal o MercadoPago): la única con próximo cobro y pastilla. */
export function isAutomaticMethod(methodKey) {
    return methodKey === 'paypal_subscription' || methodKey === 'mp_subscription';
}

export function isCouponMethod(methodKey) {
    return methodKey === 'paypal_link' || methodKey === 'mp_link';
}

/** «Cómo pagas», con un medio que no es la suscripción automática. `null` si no hay qué decir. */
export function howYouPayKey(methodKey) {
    if (isCouponMethod(methodKey) || methodKey === 'payoneer') return 'invoice_with_link';
    if (methodKey === 'transfer') return 'invoice_transfer';
    return null;
}

export function providerName(provider) {
    if (provider === 'paypal') return 'PayPal';
    if (provider === 'mercadopago') return 'MercadoPago';
    return '';
}

/** La pastilla de la forma de pago (contrato §5): `active`, `warning`, `cancelled`; el resto, ninguna. */
export function subscriptionPillOf(paymentSubscription) {
    const status = paymentSubscription && paymentSubscription.status;
    return ['active', 'warning', 'cancelled'].includes(status) ? status : null;
}

/** El próximo cobro automático, o `null` (otro medio, sin fecha, cancelada o el pedido falló). */
export function nextChargeOf(paymentSubscription) {
    if (!paymentSubscription || !paymentSubscription.next_payment_date) return null;
    if (!['paypal', 'mercadopago'].includes(paymentSubscription.provider)) return null;
    if (paymentSubscription.status === 'cancelled') return null;
    return {date: paymentSubscription.next_payment_date, provider: paymentSubscription.provider};
}

/** El último cobro del proveedor, o `null`. */
export function lastChargeOf(paymentSubscription) {
    const last = paymentSubscription && paymentSubscription.last_payment;
    if (!last || !last.date) return null;
    return {date: last.date, amount: last.amount, currency: last.currency};
}

/**
 * El monto que acompaña al «Próximo cobro», o `null` (va la fecha sola). Lo cobra el PROVEEDOR con el monto de
 * su plan, que puede no ser el del convenio (C-sp-fe-02): si el último cobro vino en otra moneda o por otro
 * monto, el del convenio no se afirma.
 */
export function nextChargeAmountOf(price, paymentSubscription) {
    if (!price) return null;
    const last = lastChargeOf(paymentSubscription);
    if (last && last.currency && String(last.currency).toUpperCase() !== price.currency) return null;
    if (last && isNumber(last.amount) && Math.abs(last.amount - price.amount) >= 0.005) return null;
    return price.amount;
}

/**
 * La moneda en que factura y si lleva IVA, o `null`. Sale SÓLO de `price` (Tesla, contrato §9): el IVA depende
 * del tipo de factura (A, B y E lo llevan; X no), así que sin `price` no se afirma nada (C-sp-fe-03).
 */
export function currencyKeyOf(price) {
    if (!price || typeof price.plusVat !== 'boolean') return null;
    if (price.currency === 'USD') return price.plusVat ? 'currency_usd_vat' : 'currency_usd';
    if (price.currency === 'ARS') return price.plusVat ? 'currency_ars' : 'currency_ars_no_vat';
    return null;
}

/**
 * Los datos para transferir. Tesla los manda como HTML con `<br>` (`get_bank_transfer_data_html`): se
 * parten en renglones de TEXTO, sin interpretar el marcado, y cada uno en «rótulo: valor» si lo trae.
 */
export function bankTransferLines(html) {
    if (!html) return [];
    const entities = {'&amp;': '&', '&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'"};
    return String(html)
        .split(/<br\s*\/?>|\r?\n/i)
        .map((line) => line.replace(/<[^>]*>/g, '').replace(/&(amp|nbsp|lt|gt|quot|#39);/g, (m) => entities[m]).trim())
        .filter(Boolean)
        .map((line) => {
            const colon = line.indexOf(':');
            return colon > 0
                ? {label: line.slice(0, colon).trim(), value: line.slice(colon + 1).trim()}
                : {label: null, value: line};
        });
}

// ── Pagos y facturas ───────────────────────────────────────────────────────────────────────────

function isPaidLegacy(liquidation) {
    return liquidation.payment_status_manual === 'Pago' || liquidation.payment_status_auto === 'Pago';
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Días enteros desde una fecha hasta hoy, o `null`. */
export function daysSince(value, today = new Date()) {
    const date = parseDate(value);
    if (!date) return null;
    return Math.max(0, Math.round((startOfDay(today) - startOfDay(date)) / 86400000));
}

/**
 * El estado de una factura, de `display_state` (Tesla, la misma definición de deuda que la cobranza). Sin
 * `display_state` (Tesla viejo) vale la regla de siempre: pagada o pendiente; nunca «vencida».
 */
export function liquidationStateOf(liquidation, today = new Date()) {
    switch (liquidation.display_state) {
        case 'paid':
            return {kind: 'paid', date: liquidation.paid_on || null};
        case 'overdue':
            return {kind: 'overdue', dueDate: liquidation.due_date || null, days: daysSince(liquidation.due_date, today)};
        case 'pending':
            return {kind: 'pending', dueDate: liquidation.due_date || null};
        default:
            return isPaidLegacy(liquidation)
                ? {kind: 'paid', date: null, legacy: true}
                : {kind: 'pending', dueDate: null, legacy: true};
    }
}

export function isUnpaid(liquidation) {
    return liquidationStateOf(liquidation).kind !== 'paid';
}

/** ¿Hay algo por pagar? Gobierna el refresco de 30 s y «Actualizar estado». */
export function hasUnpaid(liquidations) {
    return (liquidations || []).some(isUnpaid);
}

/** La factura vencida que va arriba de la vista (la más vieja) y cuántas hay, o `null`. */
export function overdueOf(liquidations) {
    const overdue = (liquidations || []).filter((liquidation) => liquidation.display_state === 'overdue');
    if (overdue.length === 0) return null;
    const key = (liquidation) => String(liquidation.due_date || liquidation.month_created || '9999');
    const oldest = overdue.slice().sort((a, b) => key(a).localeCompare(key(b)))[0];
    return {oldest, count: overdue.length};
}

/** El cupón que se puede pagar ahora (link de PayPal o MercadoPago), o `null`. */
export function payableCouponOf(liquidation) {
    return (liquidation.payment_cupons || []).find(
        (coupon) => coupon.simplified_status === 'pending' && coupon.payment_url,
    ) || null;
}

/** El monto de la factura en la moneda del convenio (el mismo criterio que la tabla de siempre). */
export function liquidationAmountOf(liquidation) {
    const amount = liquidation.amount_in_currency !== null && liquidation.amount_in_currency !== undefined
        ? liquidation.amount_in_currency
        : liquidation.invoice_total;
    return {amount, currency: liquidation.currency};
}

export const RECENT_MONTHS = 3;

/**
 * Las tres más recientes a la vista y el resto detrás de «Ver los N meses anteriores». Una factura sin
 * pagar NUNCA queda escondida, aunque sea vieja: es justo la que hay que ver.
 */
export function splitLiquidations(liquidations) {
    const list = liquidations || [];
    const visible = [];
    const hidden = [];
    list.forEach((liquidation, index) => {
        if (index < RECENT_MONTHS || isUnpaid(liquidation)) visible.push(liquidation);
        else hidden.push(liquidation);
    });
    return {visible, hidden};
}

const ES_MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre',
    'octubre', 'noviembre', 'diciembre'];

/** `period_as_text` de Tesla («Octubre - 2026») como fecha del primer día, o `null`. */
function monthOfPeriodText(text) {
    const match = /^\s*([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s*-\s*(\d{4})\s*$/.exec(text || '');
    if (!match) return null;
    const index = ES_MONTHS.indexOf(match[1].toLowerCase());
    return index >= 0 ? new Date(Number(match[2]), index, 1) : null;
}

/** «01/10/2026 - 31/10/2026» como rango de fechas, o `null`. */
function rangeOfPeriodText(text) {
    const match = /^\s*(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2})\/(\d{2})\/(\d{4})\s*$/.exec(text || '');
    if (!match) return null;
    return {
        from: new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])),
        to: new Date(Number(match[6]), Number(match[5]) - 1, Number(match[4])),
    };
}

/**
 * El período de una factura en el idioma de la pantalla. Tesla lo escribe en español («Octubre - 2026»):
 * se reconoce y se vuelve a escribir; un rango de fechas va tal cual (no tiene idioma).
 */
export function periodLabelOf(liquidation, lang) {
    const month = monthOfPeriodText(liquidation.period_as_text);
    if (month) return formatMonth(month, lang);
    if (liquidation.period_as_text) return liquidation.period_as_text;
    const created = parseDate(liquidation.month_created);
    return created ? formatMonth(created, lang) : '';
}

/** El período en curso (el de la factura de este mes), o `null` si no hay ninguna. */
export function currentPeriodOf(liquidations, lang, today = new Date()) {
    const day = startOfDay(today);
    const current = (liquidations || []).find((liquidation) => {
        const month = monthOfPeriodText(liquidation.period_as_text);
        if (month) return month.getFullYear() === day.getFullYear() && month.getMonth() === day.getMonth();
        const range = rangeOfPeriodText(liquidation.period_as_text);
        return !!range && range.from <= day && day <= range.to;
    });
    return current ? periodLabelOf(current, lang) : null;
}

// ── Correos de facturación ─────────────────────────────────────────────────────────────────────

export const MAX_INVOICE_EMAILS = 5;
export const MAX_EMAILS_CHARS = 300;
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/**
 * La misma regla que Tesla (contrato §1.3 y §9): separados por coma o punto y coma, cada uno con formato de
 * correo, hasta `max` y 300 caracteres. Se normalizan sin espacios y en minúscula, y los repetidos se SACAN
 * (no son error). Vacío no vale para las facturas; sí para el link de pago (`allowEmpty`: el cupón va
 * entonces a los correos de facturas).
 */
export function checkEmails(raw, {max = MAX_INVOICE_EMAILS, allowEmpty = false} = {}) {
    // Un salto de línea pegado en el área de texto también separa; lo que se manda va siempre con coma.
    const parts = [...new Set(String(raw || '').split(/[,;\r\n]/).map((part) => part.trim().toLowerCase()).filter(Boolean))];
    const invalid = parts.filter((email) => !EMAIL_PATTERN.test(email));
    const value = parts.join(',');
    const tooMany = parts.length > max || value.length > MAX_EMAILS_CHARS;
    const empty = parts.length === 0;
    return {
        emails: parts,
        value,
        invalid,
        empty,
        tooMany,
        ok: (allowEmpty || !empty) && !tooMany && invalid.length === 0,
    };
}

/** Los correos guardados, para mostrar: «a@b.com, c@d.com». */
export function displayEmails(raw) {
    return String(raw || '').split(/[,;]/).map((part) => part.trim()).filter(Boolean).join(', ');
}

// ── Los textos del chat (copy.md §9) ───────────────────────────────────────────────────────────

function periodWord(t, period) {
    return String(t(`agency_subscription.period_${period}`)).toLowerCase();
}

/** «Quiero cambiar mi plan de Agency a Big Agency, con pago mensual (USD 240 por mes).» + comentario. */
export function planChangeMessage(t, {from, to, period, price, comment}) {
    const base = t(`${NS}.chat.plan_change`, {from, to, period: periodWord(t, period), price, interpolation: RAW});
    const extra = String(comment || '').trim();
    return extra ? `${base} ${t(`${NS}.chat.comment_suffix`, {comment: extra, interpolation: RAW})}` : base;
}

export function annualMessage(t, {plan, price, total}) {
    return t(`${NS}.chat.annual`, {plan, price, total, interpolation: RAW});
}

/** «Quiero sumar un usuario más a mi plan (ARS 16.400 + IVA por mes).», en la moneda que se estaba mirando. */
export function moreUsersMessage(t, {currency, lang} = {}) {
    return t(`${NS}.chat.more_users`, {price: addonPriceText(t, 'user', {currency, lang}), interpolation: RAW});
}

export function morePortalMessage(t, {used, limit}) {
    return t(`${NS}.chat.more_portal`, {used, limit, interpolation: RAW});
}

export function billingIdentityMessage(t) {
    return t(`${NS}.chat.billing_identity`);
}

export function paymentMethodMessage(t, {method}) {
    return t(`${NS}.chat.payment_method`, {method, interpolation: RAW});
}

export function customPlanMessage(t) {
    return t(`${NS}.chat.custom_plan`);
}

export function paymentProblemMessage(t, {period}) {
    return t(`${NS}.chat.payment_problem`, {period, interpolation: RAW});
}
